import { useAtom, useAtomValue } from 'jotai';

import { postcodeAtom } from '@core/Atoms/Postcode/Postcode.atom';
import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import { locationAtom } from '@core/Atoms/Search/LocationAtom';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import { getModelNameFromId } from '@core/Services/Search/Utils/GetModelNameFromId';
import { getBasePathFromRoot } from '@core/Utils/Search/GetBasePathFromRoot';
import { getCorrectRelevanceForUk } from '@core/Utils/SearchFilters/AddDefaultSortingIfMissing';

import { areaAtom } from '../Atoms/AreaAtom';
import { makeAtom } from '../Atoms/MakeAtom';
import { modelAtom } from '../Atoms/ModelAtom';
import { getPartsFromPlpPath } from '../Utils/GetPartsFromPlpPath';
import { useGetRoot } from './UseGetRoot';

const useSearchCtaUrl = (
  postcodeRequired: boolean,
  locale: Locale,
  plpPath?: string,
  isLocationHub?: boolean,
  experiments?: { variationId: string; experimentId: string }[],
) => {
  const root = useGetRoot();
  const [selectedMake] = useAtom(makeAtom);
  const [selectedModel] = useAtom(modelAtom);
  const { isValid, location, error: hasError } = useAtomValue(locationAtom);
  const postcode = useAtomValue(postcodeAtom);
  const { totalCount } = useAtomValue(aggregationsAtom);
  const { FINAL_DEFAULT_SORTING } = getCorrectRelevanceForUk(experiments);
  const isAMakeSelected = selectedMake && selectedMake.key !== 'any_make';
  const isAModelSelected = selectedModel && selectedModel.key !== 'any_model';
  const selectedArea = useAtom(areaAtom);
  //plpPath is a string with query params e.g 'ecom=true' or 'ecom=true&sort=price-asc'
  const isOldStylePlpPath = !!plpPath && plpPath.indexOf('/') !== 0;
  const cleanPlpPath = !plpPath || isOldStylePlpPath ? undefined : getPartsFromPlpPath(plpPath);

  const getMakeModelSlug = () => {
    const modelIdForSlug = getModelNameFromId(selectedModel.key);
    const makeKey = (isAMakeSelected && `/${selectedMake.key}`) || '';
    const modelKey = (isAModelSelected && `/${modelIdForSlug}`) || '';
    return makeKey + modelKey;
  };

  const getUserLocParams = (distance?: number): string => {
    // only check the lat and lon when API returns location
    if (isValid) {
      const { lat, lon } = location;
      const correctPC = postcode[locale];
      if (lat && lon && correctPC) {
        let queryString = `lat=${lat}&lon=${lon}&postcode=${correctPC}`;
        if (distance) {
          queryString += `&max-distance=${distance}`;
        }

        return queryString;
      }
    }
    return '';
  };

  const getLocationHubPath = () => {
    if (selectedArea[0].key !== 'any_area') return `/${selectedArea[0].key}`;

    return ``;
  };

  const getPlpType = () => {
    if (cleanPlpPath) {
      return `${getBasePathFromRoot(root)}`;
    } else {
      return '/cars/used';
    }
  };

  const filtersSlug = () => {
    if (isLocationHub) {
      return getLocationHubPath();
    }

    const makeModel = getMakeModelSlug();

    if (cleanPlpPath?.filtersSlug) {
      return `${makeModel}/${cleanPlpPath?.filtersSlug}`;
    }

    return makeModel;
  };

  const getQueryParams = () => {
    if (isLocationHub) {
      return '';
    }

    const userLocQueryStr = new URLSearchParams(getUserLocParams(undefined));
    const plpPathQueryStr = new URLSearchParams(
      isOldStylePlpPath ? plpPath : cleanPlpPath?.queryStr,
    );

    // Sort can be overwritten from CMS
    if (!plpPathQueryStr.has('sort') && FINAL_DEFAULT_SORTING[locale]) {
      plpPathQueryStr.append('sort', FINAL_DEFAULT_SORTING[locale]!);
    }

    // User location data can NOT be overwritten from CMS
    plpPathQueryStr.delete('lat');
    plpPathQueryStr.delete('lon');
    plpPathQueryStr.delete('postcode');

    //if userLocQueryStr has lat it will always have lon and postcode
    if (userLocQueryStr.has('lat')) {
      plpPathQueryStr.append('lat', userLocQueryStr.get('lat')!);
      plpPathQueryStr.append('lon', userLocQueryStr.get('lon')!);
      plpPathQueryStr.append('postcode', userLocQueryStr.get('postcode')!);
    }
    //max-distance may or may not be in userLocQueryStr
    if (userLocQueryStr.has('max-distance'))
      plpPathQueryStr.append('max-distance', userLocQueryStr.get('max-distance')!);

    const queryStr = plpPathQueryStr.toString();
    return queryStr ? `?${queryStr}` : '';
  };

  const getSearchListingURL = () => {
    return `${getPlpType()}${filtersSlug()}${getQueryParams()}`;
  };

  const getIsFormValid = () => {
    const isPostCodeValid = postcodeRequired ? !hasError && isValid : true;
    return isLocationHub ? true : totalCount > 0 && isPostCodeValid;
  };

  return { searchUrl: getSearchListingURL(), isFormValid: getIsFormValid() };
};

export { useSearchCtaUrl };
